import React, { lazy, Suspense, useEffect } from 'react';

import { AppRouter } from './AppRouter/AppRouter';
import { CookieModalGate } from './AppRouter/CookieModal/CookieModalGate';
import { isLocaleLandingRoute } from './AppRouter/landingRouteUtils';
import { RouteLoadingFallback } from './AppRouter/RouteLoadingFallback';
import {
  isLoginRoute,
  isPublicAuthRoute,
} from './AppRouter/PublicRoutes/authRouteUtils';
import { AuthRoutesLoading } from './AppRouter/PublicRoutes/AuthRoutesLoading';
import './App.scss';

import { UIMeasure } from './components/shared/UIMeasure/UIMeasure';
import { FeatureToggle } from './components/shared/FeatureToggle/FeatureToggle';
import { Route } from 'react-router-dom';

import { preloadApolloAppContent } from '@xarvio/frontend-core-app-wrapper/lib/ApolloAppContentLoader';
import { AppWrapper } from '@xarvio/frontend-core-app-wrapper/lib/AppWrapper';
import { panoramaCallbackRoutePath } from '@xarvio/frontend-core-platform/lib/services/panorama/panoramaCallback';
import {
  UseGetPageUrl,
  UseGeneratePageUrl,
} from '@xarvio/frontend-core-router/lib/types';
import FEATURES_CONFIG from '@xarvio/frontend-core-state/lib/featureFlags/app-features-config.json';
import { useGetPageUrl, useGeneratePageUrl } from './AppRouter/pageUrlHooks';
import {
  loadAcceptTermsAndConditionsPage,
  loadEmailVerifiedPage,
} from './AppRouter/PublicRoutes/authPageRemotes';

const HotjarFeedbackWidget = lazy(() =>
  import('./components/shared/HotjarFeedbackWidget/HotjarFeedbackWidget').then(
    (m) => ({ default: m.HotjarFeedbackWidget }),
  ),
);
const StagingPanelGate = lazy(() =>
  import('./components/shared/StagingPanel/StagingPanelGate').then((m) => ({
    default: m.StagingPanelGate,
  })),
);
const EmailVerifiedPage = lazy(loadEmailVerifiedPage);
const AcceptTermsAndConditionsPage = lazy(loadAcceptTermsAndConditionsPage);

const loadMomentLocale = async (locale: string) => {
  const momentUtils = await import('./utils/momentUtils');
  await momentUtils.loadMomentLocale(locale);
};

const appElement = (
  <>
    <AppRouter />
    <UIMeasure />
    <FeatureToggle featureName={'HOTJAR_FEEDBACK'}>
      <Suspense fallback={null}>
        <HotjarFeedbackWidget />
      </Suspense>
    </FeatureToggle>
  </>
);

const App: React.FC<React.PropsWithChildren<unknown>> = () => {
  // Await each import so chunks parse one at a time, yielding to the main thread (effective in production bundles; Vite dev fans out per-module).
  useEffect(() => {
    const delay = (ms: number) =>
      new Promise<void>((resolve) => setTimeout(resolve, ms));

    const prefetch = async () => {
      void preloadApolloAppContent();
      await import('./AppRouter/PrivateRoutes/PrivateRoutes');
      await delay(500);
      await import(
        './AppRouter/DroneImageryUploadQueueContext/DroneImageryUploadQueueProvider'
      );
      await delay(500);
      await import('./pages/NewStatusPage/NewStatusPage');
    };

    const handler = () => {
      prefetch();
      window.removeEventListener('prefetch-private-routes', handler);
    };
    window.addEventListener('prefetch-private-routes', handler);
    return () => window.removeEventListener('prefetch-private-routes', handler);
  }, []);

  return (
    <AppWrapper
      loadMomentLocale={loadMomentLocale}
      featuresConfig={FEATURES_CONFIG}
      isFullPageOverlayEnabled={(pathname) => !isPublicAuthRoute(pathname)}
      isApolloEnabled={(pathname) => !isLoginRoute(pathname)}
      shouldRenderWhileInitDataLoading={isLocaleLandingRoute}
      persistentChrome={
        <Suspense fallback={null}>
          <StagingPanelGate />
        </Suspense>
      }
      appChrome={
        <FeatureToggle featureName="COOKIE_CONSENT_MODAL">
          <CookieModalGate />
        </FeatureToggle>
      }
      loadingFallback={<RouteLoadingFallback />}
      useGetPageUrl={useGetPageUrl as unknown as UseGetPageUrl}
      useGeneratePageUrl={useGeneratePageUrl as unknown as UseGeneratePageUrl}
      unprefixedRedirectPaths={[panoramaCallbackRoutePath]}
      emailVerifiedRouteElement={
        <Suspense fallback={<AuthRoutesLoading />}>
          <EmailVerifiedPage />
        </Suspense>
      }
      acceptTermsRouteElement={
        <Suspense fallback={<AuthRoutesLoading />}>
          <AcceptTermsAndConditionsPage />
        </Suspense>
      }
    >
      <Route path="*" element={appElement} />
    </AppWrapper>
  );
};

export default App;
